import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { FileDownLoader } from "../../../src/taskpane/components/helpers/fileDownloader";
import { removeExtension } from "../../../src/taskpane/components/helpers/stringHelper";
import { extensions, SharePointFolderContentTypeIds, validPdfExtensions } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { getFileItemIdFromRelativePath, getFilePdfByteContent, getItemWebViewUrl } from "../../../src/taskpane/services/GraphService";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { getFileNameDownloadTemplate, getItemCurrentVersion, getItemFieldTextValue, getWildcardValues, sanitizeDownloadFileName, versionLabelInternalName } from "../../../utilities/FileNameDownloadTemplateHelper";

export function useMenuItem_DownloadAsPDF(
  listFields: any,
  selectedItems: any[],
  waitFor: boolean,
  setWaitFor: (waitFor: boolean) => void,
  isInOneDrive: boolean,
  setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
  setErrorMessage?: (errorMessage: string) => void) {
  const languageStrings = React.useContext(LanguageContext);

  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);

  const [menuItem_DownloadAsPDF, setMenuItem_DownloadAsPDF] = React.useState<IContextualMenuItem>();

  React.useEffect(() => {
    if (selectedItems?.length === 1
      && selectedItems[0].EffectiveBasePermissions?.canOpenItems
      && !selectedItems[0].ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)
      && validPdfExtensions.some(validExt => selectedItems[0][".fileType"]?.replace(".", "")?.toLowerCase() === validExt)) {
      setMenuItem_DownloadAsPDF(
        {
          key: "downloadAsPDF",
          disabled: (waitFor === true || isBusySaving === true),
          text: `${languageStrings.DownloadAsPdf}`,
          iconProps: { iconName: "PDF" },
          onClick: () => {
            downloadFileAsPDF(selectedItems[0]);
          }
        }
      );
    } else {
      setMenuItem_DownloadAsPDF(null);
    }
  }, [selectedItems, waitFor, currentDirectory, oneDriveRootDetails]);

  const downloadFileAsPDF = async (selectedItem: any) => {
    setWaitFor(true);

    try {
      let listId: string;
      let webUrl: string;
      let fileNameTemplateString: string;
      let wildcardValues: string[];
      if (!isInOneDrive) {
        listId = currentSharepointFolder?.sharepointListId ?? currentDirectory?.sharePointListId;
        webUrl = currentDirectory?.webUrl;

        fileNameTemplateString = getFileNameDownloadTemplate(siteInfo, currentDirectory?.siteDisplayName, currentDirectory?.DocumentLibrary);
        if (fileNameTemplateString) {
          wildcardValues = getWildcardValues(fileNameTemplateString);
        }
      }

      const driveId = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
      const spItemUrlparts = selectedItem['.spItemUrl'].split('/');
      let itemId: string = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
      if (!itemId) {
        const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
        const folderUrl = new URL(driveUrl.origin + selectedItem.FileRef);
        itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
      }

      const [pdfContent, fileName] = await Promise.all([
        getFilePdfByteContent({ id: itemId, driveId: driveId }),
        (async () => {
          if (fileNameTemplateString && Array.isArray(wildcardValues) && wildcardValues.length > 0 && listFields) {
            let itemValues: any;
            if (selectedItem && webUrl && listId) {
              const itemUniqueId: string = selectedItem?.UniqueId?.replace("{", "").replace("}", "");
              if (itemUniqueId) {
                const itemCurrentVersion = await getItemCurrentVersion(await authModule.getSharePointAccessToken(), webUrl, listId, itemUniqueId);
                if (itemCurrentVersion) {
                  itemValues = itemCurrentVersion;
                }
              }
            }

            let fileName: string = fileNameTemplateString;
            wildcardValues.forEach(wildcardValue => {
              const field = wildcardValue === versionLabelInternalName
                ? { InternalName: versionLabelInternalName }
                : listFields.value.find(x => x.Title === wildcardValue || x.InternalName === wildcardValue);

              fileName = fileName.replaceAll(`<<[${wildcardValue}]>>`, getItemFieldTextValue(itemValues ?? selectedItem, field) ?? "");
            });
            return (fileName += "." + extensions.pdf);
          }
          else {
            return `${removeExtension(selectedItem.FileLeafRef)}.${extensions.pdf}`;
          }
        })()
      ]);

      if (!pdfContent) {
        setIsCMErrorModalOpen(true);
        setErrorMessage(languageStrings.DownloadAsPdfFailure);
      }
      else {
        FileDownLoader.downloadFile(pdfContent, sanitizeDownloadFileName(fileName));
      }
    } catch (error) {
      logger(error, "useMenuItem_DownloadAsPDF.tsx (downloadFileAsPDF)");
      setIsCMErrorModalOpen(true);
      setErrorMessage(languageStrings.DownloadAsPdfFailure);
    }

    setWaitFor(false);
  }

  return {
    menuItem_DownloadAsPDF
  }
}